import {
  type FieldFunctionOptions,
  InMemoryCache,
  type StoreObject,
} from "@apollo/client";

import type {
  GetEobsQueryV2QueryVariables,
  QueryIssuesV2QueryVariables,
} from "@medbillai/generated-graphql-types";

export const mergeEOBS: (
  existing: StoreObject[] | undefined,
  incoming: StoreObject[] | undefined,
  options: FieldFunctionOptions<GetEobsQueryV2QueryVariables>,
) => StoreObject[] | undefined = (existing, incoming, { args, readField }) => {
  const merged = existing ? existing.slice(0) : [];
  const incomingSet = incoming ?? [];
  const after = args?.after ?? 0;

  for (let i = 0; i < incomingSet.length; ++i) {
    const item = incomingSet[i];
    if (item !== undefined) {
      merged[after + i] = item;
    }
  }

  const mergedFilter = [];
  const mergedIdSet = new Set();
  // Edge Case: If the order of EOBs changes between the initial fetch and subsequent fetches,
  // there may be duplicate IDs in the list. This logic ensures any duplicates are handled.
  // Note: The next request will still work correctly because the `after` variable is recalculated
  // based on the current list length, not the previously returned value.
  for (const item of merged) {
    const itemId = readField("id", item);
    if (!mergedIdSet.has(itemId)) {
      mergedIdSet.add(itemId);
      mergedFilter.push(item);
    }
  }
  return mergedFilter;
};

export const mergeIssues: (
  existing: StoreObject[] | undefined,
  incoming: StoreObject[] | undefined,
  options: FieldFunctionOptions<QueryIssuesV2QueryVariables>,
) => StoreObject[] | undefined = (existing, incoming, { readField }) => {
  const merged = existing ? existing.slice(0) : [];
  const existingIdSet = new Set(merged.map(issue => readField("id", issue)));
  // Remove incoming tasks already present in the existing data.
  const incomingAdjusted = incoming
    ? incoming.filter(task => !existingIdSet.has(readField("id", task)))
    : [];
  // This is a group of all of the items that fit our search for everything, so
  // we can update this response and it will directly impact the read results
  merged.push(...incomingAdjusted);
  return merged;
};

/**
 * This theoreticlly will lose performance as it gets bigger, but for the sake
 * of having concurrent updates accross all filters, the case, and the cases screen
 * i think it is worth having. We could implement ID ejection to shrink the
 * total size and not keep the extra paginated values if this is a concern.
 */
export const readIssues: (
  existing: StoreObject[] | undefined,
  options: FieldFunctionOptions<QueryIssuesV2QueryVariables>,
) => StoreObject[] | undefined = (existing, { args, readField }) => {
  if (!existing) {
    return;
  }
  const current = existing ? existing.slice(0) : [];

  const { closed, unread, read, open } = args || {};

  const hasAnyFilter = closed || read || unread || open;

  const filteredItems = current.filter((issue: StoreObject) => {
    if (!hasAnyFilter) {
      return false;
    }

    const closedAt = readField("closedAt", issue);
    const firstUnseenEvent = readField("firstUnseenEvent", issue);
    const isClosed = Boolean(closedAt);
    const isOpen = !closedAt;
    const isUnread = Boolean(firstUnseenEvent);
    const isRead = !firstUnseenEvent;

    if (closed && isClosed) {
      return true;
    }
    if (read && unread && isOpen) {
      return true;
    }
    if (read && !unread && isOpen && isRead) {
      return true;
    }
    if (!read && unread && isOpen && isUnread) {
      return true;
    }
    if (!read && !unread && open && isOpen) {
      return true;
    }
    return false;
  });

  const sortedItems = filteredItems.sort((a: StoreObject, b: StoreObject) => {
    const dateA = new Date(
      readField("sentAt", readField("lastEvent", a)) || 0,
    ).getTime();
    const dateB = new Date(
      readField("sentAt", readField("lastEvent", b)) || 0,
    ).getTime();

    return dateB - dateA;
  });

  return sortedItems;
};

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        queryIssuesV2: {
          keyArgs: [],
          merge: mergeIssues,
          read: readIssues,
        },
        queryExplanationOfBenefitsV2: {
          keyArgs: ["inNetwork", "outOfNetwork", "denied"],
          merge: mergeEOBS,
        },
      },
    },
    User_Type: {
      fields: {
        issues: {
          keyArgs: ["filter"], // separates unread vs unfiltered, etc.
          merge: (
            _existing: StoreObject[] | undefined,
            incoming: StoreObject[] | undefined,
          ): StoreObject[] | undefined => incoming,
        },
      },
    },
    issue__Issue_Type: {
      fields: {
        userVisibleEvents: {
          // Always replace — rapid streaming cache writes would cause duplicates.
          merge: (
            _existing: StoreObject[] | undefined,
            incoming: StoreObject[] | undefined,
          ): StoreObject[] | undefined => incoming,
        },
      },
    },
    // Ingestion health records key on their materialization key so the list
    // (`queryUserEobs` / `queryUserInvoices`) and detail (`queryUserEob` /
    // `queryUserInvoice`) share one cache entry.
    UserHealthRecord: {
      keyFields: ["key"],
    },
    issue__DocumentUploadedEvent_Type: {
      fields: {
        uploading: {
          // This is a client-side only field. If it is not present, we return
          // false because the document is not currently uploading.
          read: (existing: boolean = false) => existing,
        },
        uploadFailed: {
          // Client-side only flag set by onUploadError to render an in-chat
          // error alert on the optimistic event. Defaults to false.
          read: (existing: boolean = false) => existing,
        },
        previewPromptMessage: {
          // Client-side only field, only set on preview events. Real cases
          // never carry it, so default to null to keep the cache read complete.
          read: (existing: string | null = null) => existing,
        },
      },
    },
  },
});
